import { computeAlphaCoverage } from './inkOccupancy';

export class LayerSystem {
  constructor(canvas) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Failed to get 2d context');
    this.visibleCtx = ctx;

    this.bgCanvas = document.createElement('canvas');
    this.inkCanvas = document.createElement('canvas');
    this.fxCanvas = document.createElement('canvas');
    this.uiCanvas = document.createElement('canvas');

    const bgCtx = this.bgCanvas.getContext('2d');
    const inkCtx = this.inkCanvas.getContext('2d');
    const fxCtx = this.fxCanvas.getContext('2d');
    const uiCtx = this.uiCanvas.getContext('2d');

    if (!bgCtx || !inkCtx || !fxCtx || !uiCtx) {
      throw new Error('Failed to create layer contexts');
    }

    this.bgCtx = bgCtx;
    this.inkCtx = inkCtx;
    this.fxCtx = fxCtx;
    this.uiCtx = uiCtx;

    this.dpr = 1;
    this.width = 0;
    this.height = 0;

    this.marginLeftK = 0.06;
    this.marginRightK = 0.06;
    this.marginTopK = 0.08;
    this.marginBottomK = 0.08;

    this.bgTop = '#0f1a24';
    this.bgBottom = '#0a121a';
    this.frameAlpha = 0.1;
    this.grainDots = 800;
    this.grainAlpha = 0.05;
    this.safeFrameColor = 'rgba(109, 242, 255, 0.35)';
  }

  setTheme(theme) {
    this.bgTop = theme.bgTop;
    this.bgBottom = theme.bgBottom;
    this.frameAlpha = theme.frameAlpha;
    this.grainDots = theme.grainDots;
    this.grainAlpha = theme.grainAlpha;
    if (theme.safeFrameColor) this.safeFrameColor = theme.safeFrameColor;
  }

  setLayout(layout) {
    this.marginLeftK = layout.marginLeftK;
    this.marginRightK = layout.marginRightK;
    this.marginTopK = layout.marginTopK;
    this.marginBottomK = layout.marginBottomK;
  }

  getBgCtx() {
    return this.bgCtx;
  }

  getInkCtx() {
    return this.inkCtx;
  }

  getFxCtx() {
    return this.fxCtx;
  }

  getUiCtx() {
    return this.uiCtx;
  }

  composite() {
    // Composite in device-pixel space to avoid double-scaling/clipping.
    this.visibleCtx.setTransform(1, 0, 0, 1, 0, 0);
    this.visibleCtx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.visibleCtx.drawImage(this.bgCanvas, 0, 0);
    this.visibleCtx.drawImage(this.inkCanvas, 0, 0);
    this.visibleCtx.drawImage(this.fxCanvas, 0, 0);
    this.visibleCtx.drawImage(this.uiCanvas, 0, 0);
  }

  clearInk() {
    this.inkCtx.clearRect(0, 0, this.width, this.height);
  }

  clearFx() {
    this.fxCtx.clearRect(0, 0, this.width, this.height);
  }

  clearUi() {
    this.uiCtx.clearRect(0, 0, this.width, this.height);
  }

  boardArea() {
    const x = this.width * this.marginLeftK;
    const y = this.height * this.marginTopK;
    const w = this.width * (1 - this.marginLeftK - this.marginRightK);
    const h = this.height * (1 - this.marginTopK - this.marginBottomK);
    return { x, y, w, h };
  }

  getInkCoverageInRect(rect, options = {}) {
    const x = Number.isFinite(rect?.x) ? rect.x : 0;
    const y = Number.isFinite(rect?.y) ? rect.y : 0;
    const w = Math.max(0, Number.isFinite(rect?.w) ? rect.w : 0);
    const h = Math.max(0, Number.isFinite(rect?.h) ? rect.h : 0);
    if (w <= 0 || h <= 0) return 0;

    const dpr = this.dpr || 1;
    const px = Math.max(0, Math.floor(x * dpr));
    const py = Math.max(0, Math.floor(y * dpr));
    const pw = Math.max(1, Math.min(this.inkCanvas.width - px, Math.ceil(w * dpr)));
    const ph = Math.max(1, Math.min(this.inkCanvas.height - py, Math.ceil(h * dpr)));
    if (pw <= 0 || ph <= 0) return 0;

    try {
      const img = this.inkCtx.getImageData(px, py, pw, ph);
      return computeAlphaCoverage(img.data, options.alphaThreshold ?? 16, options.sampleStep ?? 2);
    } catch (_error) {
      return 0;
    }
  }

  hasInkInRect(rect, options = {}) {
    const minCoverage = options.minCoverage ?? 0.02;
    return this.getInkCoverageInRect(rect, options) >= minCoverage;
  }

  findLeastOccupiedRect(candidates, rectSize, options = {}) {
    if (!Array.isArray(candidates) || candidates.length === 0) return null;
    const rw = Math.max(1, Number.isFinite(rectSize?.w) ? rectSize.w : 1);
    const rh = Math.max(1, Number.isFinite(rectSize?.h) ? rectSize.h : 1);

    let best = null;
    let bestCoverage = Number.POSITIVE_INFINITY;

    for (const candidate of candidates) {
      const rect = { x: candidate.x, y: candidate.y, w: rw, h: rh };
      const coverage = this.getInkCoverageInRect(rect, options);
      if (coverage < bestCoverage) {
        bestCoverage = coverage;
        best = rect;
      }
      if (coverage <= (options.earlyExitCoverage ?? 0.001)) break;
    }

    return best;
  }

  resize(width, height, dpr) {
    const isMobileViewport =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(max-width: 768px)').matches;
    const requestedDpr = Number.isFinite(dpr) ? Math.max(1, dpr) : 1;
    const dprCap = isMobileViewport ? 1.5 : 2;
    let safeDpr = Math.min(requestedDpr, dprCap);

    // Protect mobile devices from OOM by capping total backing-store pixels per layer.
    const maxPixelsPerLayer = isMobileViewport ? 1_600_000 : 3_000_000;
    const projectedPixels = width * height * safeDpr * safeDpr;
    if (projectedPixels > maxPixelsPerLayer && width > 0 && height > 0) {
      const scale = Math.sqrt(maxPixelsPerLayer / projectedPixels);
      safeDpr = Math.max(1, safeDpr * scale);
    }

    this.width = width;
    this.height = height;
    this.dpr = safeDpr;

    // Use Math.round to ensure pixel-perfect dimensions
    const pw = Math.round(width * safeDpr);
    const ph = Math.round(height * safeDpr);

    [this.bgCanvas, this.inkCanvas, this.fxCanvas, this.uiCanvas].forEach((canvas) => {
      canvas.width = pw;
      canvas.height = ph;
    });

    [this.bgCtx, this.inkCtx, this.fxCtx, this.uiCtx].forEach((ctx) => {
      ctx.setTransform(safeDpr, 0, 0, safeDpr, 0, 0);
    });

    this.canvas.width = pw;
    this.canvas.height = ph;
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;

    this.drawBackground();
    this.composite();
  }

  drawBackground() {
    const ctx = this.bgCtx;
    const { width, height } = this;
    const rootStyle =
      typeof window !== 'undefined' ? window.getComputedStyle(document.documentElement) : null;
    const token = (name, fallback) => {
      const raw = rootStyle?.getPropertyValue(name)?.trim();
      return raw || fallback;
    };
    const brand = token('--c-brand', '#6df2ff');
    const accent = token('--c-accent', '#ff7ac3');
    const bgDark = token('--c-bg-dark', '#101417');
    const surfaceDark = token('--c-surface-dark', '#0c1013');
    const borderStrong = token('--c-border-strong', 'rgba(109, 242, 255, 0.35)');

    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, this.bgTop || surfaceDark);
    gradient.addColorStop(1, this.bgBottom || bgDark);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    ctx.fillStyle = `rgba(255, 255, 255, ${this.grainAlpha})`;
    for (let i = 0; i < this.grainDots; i++) {
      const x = Math.random() * width;
      const y = Math.random() * height;
      ctx.fillRect(x, y, 1, 1);
    }

    ctx.strokeStyle = borderStrong;
    ctx.lineWidth = 8;
    ctx.strokeRect(4, 4, width - 8, height - 8);

    // Draw a visible inner safe frame: content should stay inside this boundary.
    const board = this.boardArea();
    const inset = Math.max(10, Math.min(board.w, board.h) * 0.02);
    const safeX = board.x + inset;
    const safeY = board.y + inset;
    const safeW = Math.max(1, board.w - inset * 2);
    const safeH = Math.max(1, board.h - inset * 2);

    ctx.save();
    ctx.strokeStyle = this.safeFrameColor || borderStrong;
    ctx.lineWidth = 1.5;
    ctx.strokeRect(safeX, safeY, safeW, safeH);
    ctx.restore();

    // Subtle brand corner accents to keep canvas in sync with app identity.
    const cornerSize = Math.max(14, Math.min(width, height) * 0.02);
    ctx.save();
    ctx.lineWidth = 2;
    ctx.strokeStyle = brand;
    ctx.beginPath();
    ctx.moveTo(safeX, safeY + cornerSize);
    ctx.lineTo(safeX, safeY);
    ctx.lineTo(safeX + cornerSize, safeY);
    ctx.moveTo(safeX + safeW - cornerSize, safeY);
    ctx.lineTo(safeX + safeW, safeY);
    ctx.lineTo(safeX + safeW, safeY + cornerSize);
    ctx.moveTo(safeX, safeY + safeH - cornerSize);
    ctx.lineTo(safeX, safeY + safeH);
    ctx.lineTo(safeX + cornerSize, safeY + safeH);
    ctx.moveTo(safeX + safeW - cornerSize, safeY + safeH);
    ctx.lineTo(safeX + safeW, safeY + safeH);
    ctx.lineTo(safeX + safeW, safeY + safeH - cornerSize);
    ctx.stroke();

    ctx.strokeStyle = accent;
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 6]);
    ctx.strokeRect(safeX + 4, safeY + 4, safeW - 8, safeH - 8);
    ctx.restore();
  }
}
